import { SxSReadingPaneInitializeMethod } from '../types/SxSReadingPaneStrategy';
import { composeStore } from 'owa-mail-compose-store';
import { isDumpsterSearchTable } from 'owa-mail-list-search';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getConversationReadingPaneViewState';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { lazyOpenComposeInSxS, getOrCreateSxSStoreData } from 'owa-sxs-store';
import { getActiveContentTab, lazySetTabIsShown, TabType } from 'owa-tab-store';
import { isDeepLink } from 'owa-url';
import { orchestrator } from 'satcheljs';
import {
    initializeConversationReadingPaneInSxS,
    initializeItemReadingPaneInSxS,
    initializeReadingPaneInSxSByCopyingMailReadingPane,
} from '../actions/internalActions';
import { lazyMoveComposeToTab } from 'owa-mail-compose-actions';
import getFullComposeShowingInTabContentArea from 'owa-mail-compose-actions/lib/selectors/getFullComposeShowingInTabContentArea';
import findInlineComposeViewState from 'owa-mail-compose-actions/lib/utils/findInlineComposeViewState';
import {
    getSelectedTableView,
    isDumpsterTable,
    listViewStore,
    MailRowDataPropertyGetter,
} from 'owa-mail-list-store';
import shouldShowUnstackedReadingPane from 'owa-mail-store/lib/utils/shouldShowUnstackedReadingPane';

orchestrator(initializeReadingPaneInSxSByCopyingMailReadingPane, async actionMessage => {
    // Today we don't support right hand side (FullCompose) in SxS from popout
    if (actionMessage.isProjectionPopout) {
        return;
    }

    const sxsStore = getOrCreateSxSStoreData(actionMessage.sxsId);
    if (sxsStore.shouldShow && (sxsStore.readingPaneState || sxsStore.composeId)) {
        // SxS is already showing something on the right hand side
        // do nothing
        return;
    }

    const { previewPaneSelectionSource, parentItemId, instrumentationContext } = actionMessage;
    const tableQuery = listViewStore.tableViews.get(listViewStore.selectedTableViewId)?.tableQuery;
    const isDumpsterOrDumpsterSearchTable =
        tableQuery && (isDumpsterTable(tableQuery) || isDumpsterSearchTable(tableQuery));

    let activeTab = getActiveContentTab();
    const composeViewState = getFullComposeShowingInTabContentArea();

    if (composeViewState) {
        if (isDeepLink() && window.opener) {
            // if it is a popout compose, only show the attachment preview
            // otherwise compose code will close the popout window entirely
            return;
        }

        // if compose is the primary tab, move it to a secondary tab first
        if (composeViewState.composeId === composeStore.primaryComposeId) {
            const moveComposeToTab = await lazyMoveComposeToTab.import();
            moveComposeToTab(composeViewState, true, true);
            activeTab = getActiveContentTab();
        }

        await lazySetTabIsShown.importAndExecute(activeTab, false /*isShown*/);

        const openComposeInSxS = await lazyOpenComposeInSxS.import();
        openComposeInSxS(composeViewState.composeId, actionMessage.sxsId);
        return;
    }

    const { deeplinkId } = readingPaneStore;
    const conversationReadingPaneViewState = getConversationReadingPaneViewState();

    if (deeplinkId) {
        // Deeplink
        initializeItemReadingPaneInSxS(
            {
                method: SxSReadingPaneInitializeMethod.LoadItemReadingPane,
                itemId: {
                    mailboxInfo: deeplinkId.mailboxInfo,
                    Id: deeplinkId.id,
                },
            },
            instrumentationContext,
            actionMessage.sxsId
        );
    } else if (activeTab && activeTab.type === TabType.SecondaryReadingPane) {
        const secondaryReadingPaneTabData = activeTab.data;
        if (
            secondaryReadingPaneTabData.listViewType === ReactListViewType.Message ||
            shouldShowUnstackedReadingPane()
        ) {
            initializeItemReadingPaneInSxS(
                {
                    method: SxSReadingPaneInitializeMethod.LoadItemReadingPane,
                    itemId: secondaryReadingPaneTabData.id,
                },
                instrumentationContext,
                actionMessage.sxsId
            );
        } else if (secondaryReadingPaneTabData.listViewType === ReactListViewType.Conversation) {
            initializeConversationReadingPaneInSxS(
                {
                    method: SxSReadingPaneInitializeMethod.LoadConversationReadingPane,
                    conversationId: secondaryReadingPaneTabData.id,
                    conversationSubject: secondaryReadingPaneTabData.subject,
                    conversationCategories: secondaryReadingPaneTabData.categories,
                },
                previewPaneSelectionSource,
                parentItemId,
                instrumentationContext,
                actionMessage.sxsId
            );
        }
    } else if (
        conversationReadingPaneViewState &&
        findInlineComposeViewState(conversationReadingPaneViewState.conversationId.Id)
    ) {
        // RP has inline compose open
        initializeConversationReadingPaneInSxS(
            {
                method: SxSReadingPaneInitializeMethod.LoadConversationReadingPane,
                conversationId: conversationReadingPaneViewState.conversationId,
                conversationSubject: conversationReadingPaneViewState.conversationSubject,
                conversationCategories: conversationReadingPaneViewState.conversationCategories,
            },
            previewPaneSelectionSource,
            parentItemId,
            instrumentationContext,
            actionMessage.sxsId
        );
    } else {
        const tableView = getSelectedTableView();
        const selectedRowKeys = tableView && [...tableView.selectedRowKeys.keys()];
        const isInVirtualSelectAllMode = tableView?.isInVirtualSelectAllMode;
        const selectedItemPartsCount =
            listViewStore.expandedConversationViewState.selectedNodeIds.length;
        if (
            selectedRowKeys.length == 1 &&
            selectedItemPartsCount <= 1 &&
            !isInVirtualSelectAllMode &&
            !isDumpsterOrDumpsterSearchTable
        ) {
            // 1 row selected: display reading pane only if select all is not checked and not in dumpster or dumpster search
            const rowId = MailRowDataPropertyGetter.getRowIdToShowInReadingPane(
                selectedRowKeys[0],
                tableView
            );
            const listViewType = tableView && tableView.tableQuery.listViewType;
            if (listViewType === ReactListViewType.Message || shouldShowUnstackedReadingPane()) {
                initializeItemReadingPaneInSxS(
                    {
                        method: SxSReadingPaneInitializeMethod.LoadItemReadingPane,
                        itemId: rowId,
                    },
                    instrumentationContext,
                    actionMessage.sxsId
                );
            } else if (listViewType === ReactListViewType.Conversation) {
                const rowSubject = MailRowDataPropertyGetter.getSubject(
                    selectedRowKeys[0],
                    tableView
                );
                const rowCategories = MailRowDataPropertyGetter.getCategories(
                    selectedRowKeys[0],
                    tableView
                );
                initializeConversationReadingPaneInSxS(
                    {
                        method: SxSReadingPaneInitializeMethod.LoadConversationReadingPane,
                        conversationId: rowId,
                        conversationSubject: rowSubject,
                        conversationCategories: rowCategories,
                    },
                    previewPaneSelectionSource,
                    parentItemId,
                    instrumentationContext,
                    actionMessage.sxsId
                );
            }
        }
    }
});
